/**
 * Ce que la carte « Prochain entretien » dit, en français, d'après la réponse du serveur.
 *
 * AUCUNE règle de calcul ici : le serveur décide de l'état, du point et de la date, et écrit lui-même
 * les manques et les avertissements. Ce module ne fait que dire la même chose partout (états, couleurs,
 * phrases, accords) et que montrer les nombres à la française.
 */
import type {
  CalendrierEntretien,
  EtatEntretien,
  PointAVenirEntretien,
  PointEnRetardEntretien,
  ProchainEntretien,
  RegimeEntretien,
} from '../api/client';
import { accorde } from './format';
import { dateFr, nombre } from './gammes';

/** Le badge de l'état : texte et classe de couleur (les mêmes classes que les autres badges). */
export const ETAT_ENTRETIEN: Record<EtatEntretien, { texte: string; classe: string }> = {
  A_JOUR: { texte: 'À jour', classe: 'actif' },
  BIENTOT: { texte: 'Bientôt', classe: 'attente' },
  EN_RETARD: { texte: 'En retard', classe: 'revoque' },
  A_CONFIRMER: { texte: 'À confirmer', classe: 'attente' },
  A_RENSEIGNER: { texte: 'À renseigner', classe: 'suspendu' },
};

/** « 6 000 h » — les points se disent en heures (le seul compteur de B1). */
export function heures(valeur: number): string {
  return `${nombre(valeur)} h`;
}

/** « palier B » ; sans code, « palier » seul n'aurait pas de sens : on retombe sur « point ». */
function nomDuPalier(code: string): string {
  return code ? `palier ${code}` : 'point';
}

/**
 * « Prochain : palier B à 6 000 h — dans 1 000 h, vers le 31/01/2027 ».
 * Sans date estimée, on n'invente rien : « dans 1 000 h » seulement (la carte dit ce qui manque pour une date).
 */
export function phraseProchain(prochain: PointAVenirEntretien): string {
  const debut = `${nomDuPalier(prochain.code)} à ${heures(prochain.cible)}`;
  const reste = `dans ${heures(prochain.reste)}`;
  return prochain.dateEstimee
    ? `${debut} — ${reste}, vers le ${dateFr(prochain.dateEstimee)}`
    : `${debut} — ${reste}`;
}

/** « Palier C à 12 000 h, dépassé de 400 h ». */
export function phraseRetard(retard: PointEnRetardEntretien): string {
  const nom = nomDuPalier(retard.code);
  return `${nom.charAt(0).toUpperCase()}${nom.slice(1)} à ${heures(retard.point)}, dépassé de ${heures(retard.depassement)}`;
}

/** « 4 points déjà franchis : le dernier entretien n'est pas renseigné ». */
export function phrasePointsFranchis(n: number): string {
  return `${n} ${accorde(n, 'point déjà franchi', 'points déjà franchis')} par le compteur : aucun entretien réalisé n’est enregistré, ce qu’il reste à faire est à confirmer.`;
}

/** « 1 200 h par an, d'après vos relevés » ou « … , déclarées sur la machine ». */
export function phraseRegime(regime: RegimeEntretien): string {
  return regime.origine === 'MESURE'
    ? `${heures(regime.heuresParAn)} par an, d’après les relevés du compteur`
    : `${heures(regime.heuresParAn)} par an, régime déclaré sur la machine`;
}

/** « 3 482,5 h au 12/10/2026 » — la valeur lue et sa date, avec l'unité du compteur si elle n'est pas « h ». */
export function phraseValeur(valeur: NonNullable<ProchainEntretien['valeur']>, unite: string | null): string {
  const n = Number(valeur.value);
  const nombreAffiche = Number.isFinite(n) ? nombre(n) : valeur.value;
  const u = unite && unite.trim() !== '' ? unite.trim() : 'h';
  return `${nombreAffiche} ${u} au ${dateFr(valeur.readAt)}`;
}

/** Le texte que l'on tape dans la case « heures par an » : accepte la virgule française. */
export function lireHeuresParAn(saisie: string): { valeur: number | null } | { erreur: string } {
  const texte = saisie.trim().replace(/\s/g, '').replace(',', '.');
  if (texte === '') return { valeur: null };
  if (!/^\d+(\.\d{1,2})?$/.test(texte)) {
    return { erreur: 'Saisissez un nombre d’heures par an, par exemple 3 000 ou 1 250,5.' };
  }
  const n = Number(texte);
  if (!(n > 0) || n > 8760) {
    return { erreur: 'Les heures par an doivent être comprises entre 1 et 8 760 (une année compte 8 760 heures).' };
  }
  return { valeur: n };
}

/** Le nombre tel qu'on le remet dans la case : « 3000 » et « 1250,5 » (sans séparateur de milliers). */
export function heuresParAnPourSaisie(valeur: number | null): string {
  return valeur === null ? '' : String(valeur).replace('.', ',');
}

/**
 * Le geste qui règle un manque, quand l'écran en connaît un. Le serveur écrit le message ;
 * l'écran ne propose qu'un lien vers l'endroit où l'on agit (jamais une action cachée).
 */
export type ActionManque = 'ALLER_AUX_COMPTEURS' | null;

export function actionDuManque(code: string): ActionManque {
  switch (code) {
    case 'AUCUN_COMPTEUR':
    case 'COMPTEUR_NON_DESIGNE':
    case 'AUCUN_RELEVE':
    case 'RELEVE_A_VERIFIER':
    case 'COMPTEUR_INFERIEUR_A_UN_ENTRETIEN':
      return 'ALLER_AUX_COMPTEURS';
    default:
      return null;
  }
}

/**
 * Le prochain point à venir, quand le titre de la carte parle d'autre chose : « à confirmer » (des points
 * sont déjà franchis, sans entretien enregistré) et « en retard » connaissent tous deux le prochain point,
 * et le serveur en donne la date dès que le rythme de marche est connu. Le taire laisserait ces machines
 * sans date, alors que c'est l'état de toutes celles dont aucun entretien n'est enregistré.
 * Dans les autres états le titre dit déjà le prochain point : rien à ajouter ici.
 */
export function prochainPointEnPlus(entretien: ProchainEntretien): PointAVenirEntretien | null {
  if (entretien.etat !== 'A_CONFIRMER' && entretien.etat !== 'EN_RETARD') return null;
  return entretien.prochain;
}

/**
 * Le titre de la carte, en une ligne : ce qu'il faut savoir sans rien lire d'autre.
 * L'ordre compte : un retard prime sur le reste, un point à venir ne se dit que si l'état le permet.
 */
export function resume(entretien: ProchainEntretien): string {
  // Au calendrier, la phrase est celle de l'échéance (ou du premier entretien à planifier), quel que soit l'état.
  if (estAuCalendrier(entretien) && entretien.calendrier) return phraseCalendrier(entretien.calendrier);
  switch (entretien.etat) {
    case 'EN_RETARD':
      return entretien.enRetard ? phraseRetard(entretien.enRetard) : 'Un entretien est en retard.';
    case 'BIENTOT':
    case 'A_JOUR':
      return entretien.prochain ? phraseProchain(entretien.prochain) : 'Aucun point à venir.';
    case 'A_CONFIRMER':
      return phrasePointsFranchis(entretien.pointsFranchis);
    case 'A_RENSEIGNER':
      return entretien.manques[0]?.message ?? 'Il manque une donnée pour calculer le prochain entretien.';
  }
}

// --- Au calendrier (lot B2.2, 2026-10-30) -----------------------------------------------------------

/** La machine se déclenche-t-elle au calendrier ? (une réponse d'avant le 2026-10-30 n'a pas de mode : au compteur) */
export function estAuCalendrier(entretien: Pick<ProchainEntretien, 'mode'>): boolean {
  return entretien.mode?.mode === 'CALENDAR';
}

/** « tous les 12 mois », « tous les mois ». */
export function rythmeCalendrier(mois: number): string {
  return mois === 1 ? 'tous les mois' : `tous les ${nombre(mois)} mois`;
}

/**
 * Le titre au calendrier : « Palier A le 21/08/2027 — dans 295 jours (tous les 12 mois, depuis le dernier fait le 21/08/2026) »,
 * ou « Palier A prévu le 15/09/2026 : en retard de 45 jours ». Sans entretien enregistré, aucune date (le serveur n'en invente pas).
 */
export function phraseCalendrier(c: CalendrierEntretien): string {
  const nom = nomDuPalier(c.code);
  const Nom = `${nom.charAt(0).toUpperCase()}${nom.slice(1)}`;
  if (c.echeance === null || c.depuis === null || c.joursRestants === null) {
    return `${Nom} ${rythmeCalendrier(c.mois)} : aucun entretien n’est encore enregistré, le premier reste à planifier.`;
  }
  const rythme = `${rythmeCalendrier(c.mois)}, depuis le dernier fait le ${dateFr(c.depuis)}`;
  if (c.joursRestants < 0) {
    const n = -c.joursRestants;
    return `${Nom} prévu le ${dateFr(c.echeance)} : en retard de ${nombre(n)} ${accorde(n, 'jour', 'jours')} (${rythme})`;
  }
  const quand = c.joursRestants === 0 ? 'aujourd’hui' : `dans ${nombre(c.joursRestants)} ${accorde(c.joursRestants, 'jour', 'jours')}`;
  return `${Nom} le ${dateFr(c.echeance)} — ${quand} (${rythme})`;
}

/** D'où vient le déclenchement, en une phrase (sous les boutons du choix). */
export function phraseOrigineMode(entretien: Pick<ProchainEntretien, 'mode'>): string | null {
  switch (entretien.mode?.origine) {
    case 'SANS_COMPTEUR':
      return 'Au calendrier d’office : sa gamme n’a qu’un palier et la machine n’a pas de compteur désigné. Désignez-lui un compteur pour la passer aux heures.';
    case 'GAMME':
      return entretien.mode.mode === 'CALENDAR' ? 'Au calendrier : sa gamme ne compte qu’en mois.' : 'Au compteur : sa gamme compte en heures.';
    default:
      return null;
  }
}

/** Le nombre de mois tapé : un entier de 1 à 120 ; vide = celui de la gamme (12 sinon). */
export function lireMoisCalendrier(texte: string): { valeur: number | null } | { erreur: string } {
  const t = texte.trim();
  if (t === '') return { valeur: null };
  if (!/^\d{1,3}$/.test(t)) return { erreur: 'Le nombre de mois est un entier, de 1 à 120.' };
  const n = Number(t);
  if (n < 1 || n > 120) return { erreur: 'Le nombre de mois va de 1 à 120.' };
  return { valeur: n };
}
